/**
 * Planos e preços do site do Logigate: a loja de módulos (`LojaDeModulos`, plano do Construtor 3.11)
 * no lugar da antiga `PricingSection`, que mostrava um módulo de cada vez. Pesquisa, categorias,
 * combos, a ficha de cada módulo e o carrinho com o total calculado pelo servidor
 * (`POST /criar/orcamento`, sem sessão).
 *
 * «Começar» leva ao registo com o carrinho na query (`/criar?combo=ID&extras=M:T&meses=N` ou
 * `/criar?modulos=M:T,M:T&meses=N`): com uma selecção válida, o registo abre logo nos dados da
 * empresa, e «Alterar o plano» volta à mesma loja com o carrinho carregado.
 *
 * Sempre clara, como as outras secções do site (`variante="site"`), com os cartões de combo do site.
 */
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import LojaDeModulos from "@/Logigate/componentes/planos/loja/LojaDeModulos";
import { BotaoDaLoja } from "@/Logigate/componentes/planos/loja/pecas";
import { useOrcamentoDaLoja } from "@/Logigate/componentes/planos/loja/useOrcamentoDaLoja";
import { useSelecaoDePlano } from "@/Logigate/components/planos/useSelecaoDePlano";
import { linkDeRegisto } from "@/Logigate/components/planos/selecao";
import type { Catalogo } from "@/types/planos";
import { showPop } from "@/utils/Pops";
import { CartaoComboDoSite } from "./planosDoSite";

interface Props {
    catalogo: Catalogo;
    /** Se a secção dos prestadores está na página, para a chamada no rodapé. */
    temPrestadores?: boolean;
}

export default function LojaDoSite({ catalogo, temPrestadores = false }: Props) {
    const selecao = useSelecaoDePlano(catalogo, null, catalogo.meses_disponiveis.includes(1) ? 1 : (catalogo.meses_disponiveis[0] ?? 1));
    const orcamento = useOrcamentoDaLoja("/criar/orcamento", selecao.payload, { tipo: catalogo.tipo_empresa ?? 0 });
    const temCombos = catalogo.combos.length > 0;

    const comecar = () => {
        if (selecao.vazia || !selecao.query) {
            showPop("", "Junta pelo menos um combo ou um módulo ao carrinho.", "warning");
            return;
        }
        window.location.href = linkDeRegisto(selecao.query);
    };

    return (
        <section id="precos" className="bg-gray-50 py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-10 text-center">
                    <h2 className="mb-4 text-3xl font-bold text-gray-900 sm:text-4xl">Planos e Preços</h2>
                    <p className="mx-auto max-w-2xl text-lg text-gray-500">
                        {temCombos
                            ? "Começa por um combo com tudo o que uma empresa precisa, ou junta ao carrinho só os módulos de que precisas. O total aparece ao lado, com os descontos e o IVA."
                            : "Junta ao carrinho os módulos de que precisas e paga apenas pelo que usas. O total aparece ao lado, com os descontos e o IVA."}
                    </p>
                </motion.div>

                <LojaDeModulos
                    variante="site"
                    selecao={selecao}
                    orcamento={orcamento}
                    topoDoCarrinho="lg:top-28"
                    desenharCombo={({ combo, noCarrinho, destaque, meses, onEscolher }) => (
                        <CartaoComboDoSite
                            combo={combo}
                            seleccionado={noCarrinho}
                            destaque={destaque && !noCarrinho}
                            meses={meses}
                            rotulo={noCarrinho ? "No carrinho" : "Escolher combo"}
                            onEscolher={onEscolher}
                        />
                    )}
                    accoes={
                        <div className="space-y-2">
                            <BotaoDaLoja tamanho="lg" larguraTotal icone={<ArrowRight />} onClick={comecar}>
                                Começar
                            </BotaoDaLoja>
                            <p className="text-center text-xs text-gray-500">No passo seguinte crias a empresa. Se o plano tiver custo, a factura sai nesse momento, com 7 dias para pagar.</p>
                        </div>
                    }
                />

                <p className="mt-10 text-center text-sm text-gray-400">
                    Podes juntar e tirar módulos e mudar de plano quando quiseres. Preços mensais com IVA{catalogo.iva.taxa > 0 ? ` (${catalogo.iva.taxa}%)` : ""} incluído.
                </p>
                {temPrestadores && (
                    <p className="mt-2 text-center text-sm text-gray-500">
                        Trabalhas por conta própria e só facturas serviços?{" "}
                        <button
                            type="button"
                            onClick={() => document.querySelector("#prestadores")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                            className="font-medium text-blue-600 hover:underline"
                        >
                            Vê os planos para prestadores
                        </button>
                        .
                    </p>
                )}
            </div>
        </section>
    );
}
